const DojoTransactionNotifications = require('@/lib/PaymentMethods/Dojo/DojoTransactionNotifications');
const DojoTransactionResultStatus = require('@/lib/PaymentMethods/Dojo/DojoTransactionResultStatus');
const EventNames = require('@/lib/Events/EventNames');
const Payment = require('@/lib/Payments/Payment');
const PrivilegeNames = require('@/lib/Privileges/PrivilegeNames');
const TransactionInProgress = require('@/lib/Payments/TransactionInProgress');
const { formatCurrency, formatMoney } = require('@/lib/Formatting/currency');
const { fromMinorUnit } = require('@/functions/math');
const { fromUpperSnakeCase, ucfirst } = require('@/functions/strings');
const { ModalCancelledError } = require('@/vue/modals/ModalCancelledError');
const { updateAngularUi } = require('@/functions/angular');

/**
 * This is the controller for the inner part of the payment modal for the payment method of "Dojo Chip And Pin".
 *
 * @param $filter
 * @param $scope
 * @param $timeout
 * @param {CartService} cartService
 * @param {CurrentDevice} currentDevice
 * @param {CurrencyService} currencyService
 * @param {DialogService} dialogService
 * @param {DojoDriver} dojoDriver
 * @param {LangService} langService
 * @param {PaymentMethodService} paymentMethodService
 * @param {PaymentState} paymentState
 * @param {UserService} userService
 * @param {VueModalFactory} vueModalFactory
 */
const DojoPaymentCtrl = function DojoPaymentCtrl(
    $filter,
    $scope,
    $timeout,
    cartService,
    currentDevice,
    currencyService,
    dialogService,
    dojoDriver,
    langService,
    paymentMethodService,
    paymentState,
    userService,
    vueModalFactory
) {
    $scope.paymentMethodScopes[dojoDriver.paymentMethodRef] = $scope;

    $scope.cart = cartService;
    $scope.dojoDriver = dojoDriver;
    $scope.paymentState = paymentState;
    $scope.userService = userService;

    $scope.thisPaymentMethodRef = dojoDriver.paymentMethodRef;
    /**
     * @type {PaymentMethod}
     */
    $scope.thisPaymentMethod = paymentMethodService.getByRef($scope.thisPaymentMethodRef);

    $scope.dojoTransactionStatus = DojoTransactionNotifications;
    /**
     * Has the Dojo driver been set up and is ready to use?
     *
     * @type {bool|null}
     */
    $scope.ready = null;

    /**
     * Has the transaction just been reset e.g. after a manual reconciliation.
     *
     * @type {boolean}
     */
    $scope.isReset = false;

    /**
     * @type {DojoTransaction|null}
     */
    $scope.currentTransaction = null;

    /**
     * @type {DojoTransaction[]}
     */
    $scope.allTransactions = [];

    $scope.errorMessage = null;

    $scope.signatureCheckModalID = null;

    /**
     * A message to give additional information about what to do next.
     *
     * @type {?string}
     */
    $scope.instructionMessage = null;

    /**
     * Possible state of start / restart button.
     *
     * @type {Readonly<{RESTART: string, START: string, NO_BUTTON: string}>}
     */
    $scope.startRestartButtonStates = Object.freeze({
        NO_BUTTON: 'NO_BUTTON',
        RESTART: 'RESTART',
        START: 'START',
    });

    /**
     * The current start / restart button state. This can also be used to determine the state of the secondary buttons.
     * For example, we can only set cashback if there isn't a transaction in progress and either button is showing.
     *
     * @type {string}
     */
    $scope.startRestartButtonState = $scope.startRestartButtonStates.NO_BUTTON;

    /**
     * Object to hold a css class and user facing status message.
     *
     * @type {{text: null, class: null}}
     */
    $scope.transactionDisplayMessage = {
        class: null,
        text: null,
    };

    $scope.isFinished = function isFinished() {
        // Now handle the transactionResults
        if (!$scope.currentTransaction.isFinished) {
            $scope.currentTransaction.isFinished = true;
            $scope.currentTransaction.transactionResult.class = 'message-line-danger';
            switch ($scope.currentTransaction.transactionResult.message) {
                case DojoTransactionResultStatus.SUCCESSFUL:
                    $scope.handleApprovedResponse($scope.currentTransaction);
                    paymentState.clearPaymentModalCancelable();
                    cartService.clearTransactionInProgress();
                    $scope.currentTransaction.transactionResult.class = 'message-line-success';

                    // Make sure that we don't automatically close the modal if there is message to display.
                    // e.g. signature verification timeout
                    if ($scope.currentTransaction.userMessage) {
                        $scope.preventClose();
                    }

                    // The reason this is not inside the following SIGNATURE_VERIFICATION_TIMEOUT block is because of
                    // slightly different behaviour between the virtual terminals and the test terminal. On the test
                    // terminal it is possible to have a timeout without a userMessage or SIGNATURE_VERIFICATION_TIMEOUT
                    // notification this appears to be an artifact of the Dojo test environment.
                    if ($scope.signatureCheckModalID) {
                        vueModalFactory.close($scope.signatureCheckModalID);
                        $scope.signatureCheckModalID = null;
                    }

                    if ($scope.currentTransaction.notificationStack.includes(
                        DojoTransactionNotifications.SIGNATURE_VERIFICATION_TIMEOUT
                    )) {
                        $scope.instructionMessage = 'DOJO.SIGNATURE_TIMEOUT_MESSAGE';
                    }
                    break;
                case DojoTransactionResultStatus.TIMED_OUT:
                    // If the order has timed out check the terminal.
                    break;
                case DojoTransactionResultStatus.CANCELLED:
                    paymentState.clearPaymentModalCancelable();
                    cartService.clearTransactionInProgress();
                    break;
                case DojoTransactionResultStatus.DECLINED:
                case DojoTransactionResultStatus.VOID:
                case DojoTransactionResultStatus.UNSUCCESSFUL:
                default:
                    paymentState.clearPaymentModalCancelable();
                    cartService.clearTransactionInProgress();
                    break;
            }
        }
    };

    $scope.checkSignature = function checkSignature() {
        if (!$scope.currentTransaction.isWaitingForSignatureCheck) {
            $scope.currentTransaction.isWaitingForSignatureCheck = true;
            dialogService
                .confirm(
                    'DOJO.SIGNATURE_VERIFICATION_MESSAGE',
                    {
                        cancelLabel: 'DOJO.SIGNATURE_VERIFICATION_REJECT',
                        okLabel: 'DOJO.SIGNATURE_VERIFICATION_ACCEPT',
                    },
                    (modalID) => {
                        $scope.signatureCheckModalID = modalID;
                    }
                )
                .then(async (accepted) => {
                    await dojoDriver.sendSignatureRequest($scope.currentTransaction, accepted);
                });
        }
    };

    $scope.handleTransactionResponse = function handleTransactionResponse() {
        if ($scope.currentTransaction && $scope.currentTransaction.status) {
            switch ($scope.currentTransaction.status) {
                case DojoTransactionNotifications.TRANSACTION_FINISHED:
                    $scope.isFinished();
                    break;
                case DojoTransactionNotifications.SIGNATURE_VERIFICATION:
                    $scope.checkSignature();
                    break;
                default:
                    // There are no specific actions we simply show the user the notification.
            }
        }
    };

    $scope.isTransactionInProgress = function isTransactionInProgress() {
        $scope.handleTransactionResponse();
        return $scope.currentTransaction && !$scope.currentTransaction.isFinished;
    };

    $scope.getStatus = function getStatus() {
        if ($scope.currentTransaction && $scope.currentTransaction.status) {
            return ucfirst(fromUpperSnakeCase($scope.currentTransaction.status));
        }
        return null;
    };

    $scope.cancelTransaction = async function cancelTransaction() {
        if ($scope.currentTransaction && $scope.currentTransaction.requestId) {
            await dojoDriver.sendDeleteRequest($scope.currentTransaction);
        } else {
            // There was a problem with the initial transaction request.
            $scope.ready = false;
            $scope.reset();
        }
    };

    $scope.tryAgain = () => {
        $scope.clearError();
        $scope.initiateTransaction($scope.cart.getRemainingBalance());
    };

    $scope.makePartialPayment = () => {
        $scope.showPartialPaymentModal('make', function (amount) {
            $scope.initiateTransaction(amount);
        });
    };

    $scope.initiateTransaction = async (amount, cardholderNotPresent = false) => {
        try {
            console.log('initiateTransaction...', amount);

            if (!$scope.ready) {
                throw new Error('Can\'t start Dojo transaction because config is not valid.');
            }

            if ($scope.isTransactionInProgress()) {
                throw new Error('Transaction already in progress.');
            }

            // Ensure these variables are cleared.
            $scope.isReset = false;
            $scope.signatureCheckModalID = null;

            const terminalAvailable = await $scope.isTerminalAvailable();
            if (!terminalAvailable) {
                return;
            }

            let extraPayload = {};
            if ($scope.cashbackAmount && $scope.cashbackAmount > 0 && !cardholderNotPresent) {
                extraPayload.amountCashback = $scope.cashbackAmount;
            }

            if (cardholderNotPresent) {
                extraPayload.cardholderNotPresent = true;
            }

            let dojoTransaction = dojoDriver.createTransactionRequest(amount, extraPayload);
            dojoTransaction.thirdPartyID = $scope.cart.generatePaymentThirdPartyID();

            $scope.currentTransaction = dojoTransaction;

            await $scope.sendTransaction(dojoTransaction);
        } catch (error) {
            $scope.setDojoError(error);
        }
    };

    $scope.isTerminalAvailable = async function isTerminalAvailable() {
        // Check if the terminal is ready.
        try {
            let terminalAvailable = await dojoDriver.terminalReady();
            if (!terminalAvailable) {
                throw new Error('The terminal is BUSY please try again in 10 seconds');
            }
            return true;
        } catch (error) {
            // This could be a 4xx or 5xx error from the API or just a BUSY which is not an API error but is a problem
            // as far as ProPoint is concerned.
            $scope.ready = false;
            paymentState.clearPaymentModalCancelable();
            $scope.setDojoError(error);
        }

        return false;
    };

    /**
     * @param {DojoTransaction} dojoTransaction
     */
    $scope.sendTransaction = async (dojoTransaction) => {
        $scope.transactionStarted = true;
        // Prevent closing the payment modal.
        paymentState.setPaymentModalCancelable(false, $scope.thisPaymentMethodRef);
        // Force the UI to update so the cancel button is no longer clickable.
        $timeout(() => {});
        let result = await dojoDriver.sendTransaction(dojoTransaction);
        if (result) {
            // Remember this transaction is in progress.
            await cartService.setTransactionInProgress(
                new TransactionInProgress(
                    $scope.thisPaymentMethodRef,
                    dojoTransaction.requestId,
                    dojoTransaction.amountInMajorUnits,
                )
            );

            await dojoDriver.pollTransaction(dojoTransaction);
        } else {
            paymentState.clearPaymentModalCancelable();
        }
    };

    /**
     * @param dojoTransaction
     */
    $scope.handleApprovedResponse = function handleApprovedResponse(dojoTransaction) {
        // Funds received. Add Payment to cart/order.
        let payment = new Payment();
        // Amounts from Dojo are always a +ve integer in minor units (pence or cents).
        // We use amountBase rather than amountTotal because amountTotal = amountBase + amountCashback + amountGratuity
        let amount = fromMinorUnit(dojoTransaction.latestResponse.data.amountBase);
        if (dojoTransaction.latestResponse.data.transactionType === 'REFUND') {
            amount *= -1;
        }
        payment.setTendered(amount);
        payment.setCashback(fromMinorUnit(dojoTransaction.latestResponse.data.amountCashback));
        payment.setGratuity(fromMinorUnit(dojoTransaction.latestResponse.data.amountGratuity));
        payment.setPaymentMethod($scope.thisPaymentMethod);

        payment.setStatusDetail(dojoDriver.getStatusDetail(dojoTransaction.latestResponse.data));
        payment.debugInfo = null;

        payment.setThirdPartyID(dojoTransaction.thirdPartyID);
        payment.setTransactionRef(dojoTransaction.latestResponse.data.transactionId);

        let cardNumber = dojoTransaction.latestResponse.data.primaryAccountNumber;
        payment.setLastDigits(cardNumber ? cardNumber.substr(-4) : null);

        payment.setAuthCode(dojoTransaction.latestResponse.data.authCode);
        payment.setCardTypeRef(dojoTransaction.latestResponse.data.cardSchemeName.toLowerCase());

        $scope.addPaymentAndAutoFinish(payment);
    };

    /**
     * @param {string} error
     */
    $scope.handleInvalidConfig = function handleInvalidConfig(error) {
        $scope.ready = false;
        $scope.setDojoError(error);
    };

    $scope.recoverTransaction = async function recoverTransaction(requestId, amount) {
        // Note: it is important we don't check if the terminal is ready here otherwise it will just say BUSY, and we
        // won't be able to poll the transaction.

        // Create a new DojoTransaction to hold any results and then just start polling to pick up where we
        // left off.
        let dojoTransaction = dojoDriver.createTransactionRequest(amount);
        dojoTransaction.requestId = requestId;

        $scope.currentTransaction = dojoTransaction;
        $scope.transactionStarted = true;
        // Prevent closing the payment modal.
        paymentState.setPaymentModalCancelable(false, $scope.thisPaymentMethodRef);

        try {
            // If this is a recovered transaction I don't think we should close the modal automatically.
            await dojoDriver.pollTransaction(dojoTransaction);
        } catch (error) {
            // For whatever reason we can't poll the existing transaction.
            // Display an error message saying that we were unable to recover the transaction in progress.
            // Do we need to clear the transaction in progresss?
            paymentState.clearPaymentModalCancelable();
            $scope.currentTransaction = null;
        }
    };

    $scope.setDojoError = function setDojoError(error) {
        $timeout(() => {
            $scope.errorMessage = error.message;
        });
    };

    $scope.reset = function reset() {
        cartService.clearTransactionInProgress();
        $scope.transactionInProgress = false;
        $scope.instructionMessage = null;
        $scope.currentTransaction = null;
        paymentState.clearPaymentModalCancelable();
        $scope.isReset = true;
    };

    /**
     * Should we show the start button, the restart button, or no button at all?
     *
     * @return {string}
     */
    $scope.showStartRestartButton = function showStartRestartButton() {
        const setState = (state) => {
            $scope.startRestartButtonState = state;
            return state;
        };

        if ($scope.isTransactionInProgress()) {
            return setState($scope.startRestartButtonStates.NO_BUTTON);
        }

        if ($scope.currentTransaction && $scope.currentTransaction.isFinished) {
            switch ($scope.currentTransaction.transactionResult.message) {
                case DojoTransactionResultStatus.CANCELLED:
                    return setState($scope.startRestartButtonStates.START);
                case DojoTransactionResultStatus.SUCCESSFUL:
                    if ($scope.cart.getRemainingBalance() !== 0) {
                        // There is a remaining balance after a partial payment.
                        return setState($scope.startRestartButtonStates.START);
                    }
                    break;
                case DojoTransactionResultStatus.TIMED_OUT:
                    // See: https://docs.connect.paymentsense.cloud/rest/pac-transaction-flow
                    $scope.instructionMessage = 'DOJO.MANUAL_RECONCILIATION_MESSAGE';
                    return setState($scope.startRestartButtonStates.NO_BUTTON);
                default:
                    // !CANCELLED && !SUCCESSFUL && !TIMED_OUT
                    return setState($scope.startRestartButtonStates.RESTART);
            }
        }

        if ($scope.autoStartCardTransactions) {
            if ($scope.isReset) {
                // Show the start button even when it would normally be hidden because of the autoStart. This scenario
                // happens after a transaction reset which can happen after a rejected manual reconciliation.
                return setState($scope.startRestartButtonStates.START);
            }
            return setState($scope.startRestartButtonStates.NO_BUTTON);
        }

        if (!$scope.currentTransaction) {
            return setState($scope.startRestartButtonStates.START);
        }
        return setState($scope.startRestartButtonStates.NO_BUTTON);
    };

    $scope.showCancelButton = function showCancelButton() {
        if ($scope.isTimedOut()) {
            // We want them to specifically confirm or reject the transaction.
            return false;
        }
        return $scope.startRestartButtonState === $scope.startRestartButtonStates.NO_BUTTON;
    };

    $scope.isPartialPaymentButtonDisabled = function isPartialPaymentButtonDisabled() {
        return $scope.startRestartButtonState === $scope.startRestartButtonStates.NO_BUTTON;
    };

    $scope.isCashbackButtonDisabled = function isCashbackButtonDisabled() {
        return $scope.startRestartButtonState === $scope.startRestartButtonStates.NO_BUTTON;
    };

    $scope.isCancelButtonDisabled = function isCancelButtonDisabled() {
        // It is only possible to cancel a transaction up to the point where the card has been presented. After a card
        // has been successfully presented it isn't possible to cancel the transaction. If the status is out of date
        // because of polling it is ok all it means is that the transaction won't cancel.

        if (!$scope.currentTransaction) {
            return true;
        }

        // The transaction flow usually goes:
        // 1. TRANSACTION_START
        // 2. PLEASE_WAIT
        // 3. PRESENT_CARD
        // But PLEASE_WAIT can occur at other times so we can't check for it directly.

        const lastCancelableStates = [
            DojoTransactionNotifications.BAD_SWIPE,
            DojoTransactionNotifications.PRESENT_CARD,
            DojoTransactionNotifications.PRESENT_ONLY_ONE_CARD,
            DojoTransactionNotifications.RE_PRESENT_CARD,
        ];

        if (
            lastCancelableStates.includes($scope.currentTransaction.status)
            // We haven't been asked to PRESENT_CARD yet, so it is OK to cancel.
            || !$scope.currentTransaction.notificationStack.includes(DojoTransactionNotifications.PRESENT_CARD)
        ) {
            return false;
        }

        return true;
    };

    $scope.isCardNotPresentDisabled = function isCardNotPresentDisabled() {
        if (
            !dojoDriver.config.allowCnp
            || $scope.startRestartButtonState === $scope.startRestartButtonStates.NO_BUTTON
        ) {
            return true;
        }
        return false;
    };

    $scope.startCardNotPresentPayment = async function startCardNotPresentPayment() {
        await $scope.initiateTransaction($scope.cart.getRemainingBalance(), true);
    };

    $scope.isTimedOut = function isTimedOut() {
        if (
            $scope.currentTransaction
            && $scope.currentTransaction.isFinished
            && $scope.currentTransaction.transactionResult.message === DojoTransactionResultStatus.TIMED_OUT
        ) {
            return true;
        }
        return false;
    };

    $scope.isManualReconciliationDisabled = function isManualReconciliationDisabled() {
        return !$scope.isTimedOut();
    };

    $scope.startManualReconciliation = () => {
        $scope.privilegesManager.requirePrivilege(PrivilegeNames.MANUAL_PAYMENT_RECONCILIATION).then((result) => {
            if (result.granted) {
                $scope.showManualReconciliation();
            }
        });
    };

    // Migrate this modal to use Vue.
    // @TODO https://digitickets.atlassian.net/browse/PROP-655
    $scope.showManualReconciliation = function showManualReconciliation() {
        let amount = fromMinorUnit($scope.currentTransaction.initialRequest.data.amount);
        if ($scope.currentTransaction.initialRequest.data.transactionType === 'REFUND') {
            amount *= -1;
        }

        // There must be no thousand separator used in the value in the input field.
        const moneyFormattedAmount = formatMoney(amount, 2, '.', '');
        const currencyFormattedAmount = formatCurrency(currencyService, amount);
        dialogService.prompt(
            '',
            {
                customHtml: langService.getText('DOJO.MANUAL_RECONCILIATION_MODAL_MESSAGE', { currencyFormattedAmount }),
                inputLabel: 'PAYMENT.AUTH_CODE',
                title: 'PAYMENT.MANUAL_RECONCILIATION_TITLE',
                okLabel: 'PAYMENT.MANUAL_RECONCILIATION_OK_BTN',
                okColor: 'success',
                cancelLabel: 'PAYMENT.MANUAL_RECONCILIATION_REJECT_BTN',
                cancelColor: 'danger',
                minLength: 2,
                maxLength: 9,
                keyUp: function (value) {
                    // Only letters and numbers in uppercase.
                    return value ? value.replace(/[^a-z0-9]/gi, '').toUpperCase().trim() : '';
                },
                customInputs: {
                    amount: {
                        value: moneyFormattedAmount,
                        label: 'PAYMENT.MANUAL_RECONCILIATION_AMOUNT',
                    },
                },
            }
        )
            .then((result) => {
                if (result.value) {
                    // Create a payment from the manual info.
                    const payment = new Payment();
                    payment.setTendered(result.amount);
                    payment.setAuthCode(result.value);
                    payment.setPaymentMethod($scope.thisPaymentMethod);
                    payment.setThirdPartyID($scope.currentTransaction.requestId);
                    $scope.addPaymentAndAutoFinish(payment);
                    $scope.transactionInProgress = false;
                }
            })
            .catch((error) => {
                if (error instanceof ModalCancelledError) {
                    $scope.reset();
                } else {
                    throw error;
                }
            })
            .finally(() => updateAngularUi());
    };

    /**
     * Initialization...
     */
    console.log('Dojo Payment Controller Opened');

    $scope.$on(EventNames.PAYMENT_METHOD_CHANGED, function (event, newPaymentMethod) {
        const newPaymentMethodRef = newPaymentMethod?.ref;
        if (newPaymentMethodRef === $scope.thisPaymentMethodRef) {
            console.log('Switched to Dojo');

            try {
                $scope.dojoDriver.init();
                $scope.ready = true;
            } catch (error) {
                $scope.ready = false;
                paymentState.clearPaymentModalCancelable();
                $scope.setDojoError(error);
                return;
            }

            // Check if there are abandoned transactions we need to resume.
            // This happens (for example) if the browser was refreshed while at the payment stage after the
            const txnInProgress = cartService.getTransactionInProgress();
            if (txnInProgress && txnInProgress.paymentMethodRef === $scope.thisPaymentMethodRef) {
                console.log('There are transactions to recover');

                $scope.recoverTransaction(
                    txnInProgress.txnRef,
                    txnInProgress.amount
                );
            } else {
                if ($scope.autoStartCardTransactions) {
                    // Tell the driver to initiate the transaction.
                    console.log('Switched to Dojo Chip and Pin method. Start new transaction');
                    $scope.initiateTransaction($scope.cart.getRemainingBalance());
                } else {
                    // The operator should start the transaction manually.
                }
            }
        } else {
            console.log('Switched away from Payment Dojo Chip And Pin');
        }
    });
};

module.exports = DojoPaymentCtrl;
